import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Camera, ImageSquare, PencilSimple } from "@phosphor-icons/react";
import { Input } from "./ui/input";
import WizardShell from "./wizard/WizardShell";
import ChipGroup, { Q } from "./wizard/ChipGroup";
import { useHiddenOptions } from "../lib/hiddenOptions";
import AiScanOverlay from "./AiScanOverlay";
import { detailsFrom, detailsPayload } from "./PlantDetailsFields";
import { uploadPhoto, createPlant, listPlants, fileUrl, savePlantTip } from "../lib/api";
import { HEALTH_STATUS, SOURCE_TYPES, GROWTH_STAGES } from "../lib/theme";
import { LIGHT, LIGHT_LABELS, POT_SIZES, POT_MATERIALS, SOILS } from "../lib/intakeOptions";
import RichEditor from "./RichEditor";

const EMPTY = { nickname: "", species: "", scientific_name: "", location: "", notes: "" };
const FIELD = "rounded-xl h-11 bg-muted/40 border-0";
const STEPS = [
  { key: "photo", title: "Add a plant", subtitle: "Start with a photo of the plant.", hint: "Shoot the whole plant in good light. A plain background looks best on labels." },
  { key: "identity", title: "What is it?", subtitle: "Give it a name and add its species.", hint: "Nicknames make plants easy to find. The species shows on its label." },
  { key: "home", title: "Where does it live?", subtitle: "Location, light and how it's doing today.", hint: "Your most-used spots are listed first." },
  { key: "origin", title: "Origin and pot", subtitle: "Optional, but it makes the record much richer.", optional: true, hint: "Skip this if you're in a hurry. You can fill it in later from the plant page." },
  { key: "review", title: "Review and save", subtitle: "An EAVEGRN ID is assigned when you save." },
];

function PhotoStep({ fileRef, onFile, onManual }) {
  const [drag, setDrag] = useState(false);
  return (
    <div className="pb-2">
      <button type="button" data-testid="choose-photo-btn" onClick={() => fileRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); onFile(e.dataTransfer.files?.[0]); }}
        className={`w-full aspect-[4/3] rounded-3xl flex flex-col items-center justify-center gap-3 transition-colors ${drag ? "bg-primary/10" : "bg-muted/60 hover:bg-muted"}`}>
        <span className="h-14 w-14 rounded-2xl bg-background flex items-center justify-center shadow-sm"><Camera weight="duotone" className="h-7 w-7 text-primary" /></span>
        <span className="text-sm font-medium">Take or choose a photo</span>
        <span className="text-xs text-muted-foreground">or drop an image here</span>
      </button>
      <button type="button" data-testid="add-manually-btn" onClick={onManual} className="mt-4 w-full text-center text-xs text-muted-foreground hover:text-foreground inline-flex items-center justify-center gap-1">
        <PencilSimple className="h-3.5 w-3.5" /> No photo? Add details manually
      </button>
    </div>
  );
}

const CARE_KEYS = ["cultivar", "light_exposure", "pot_size", "pot_material", "soil_mix"];

export default function AddPlantDialog({ open, onOpenChange, onCreated, template }) {
  const fileRef = useRef(null);
  const reqRef = useRef(0);
  const [step, setStep] = useState(0);
  const [phase, setPhase] = useState(null);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [photoPath, setPhotoPath] = useState(null);
  const [details, setDetails] = useState(detailsFrom());
  const [locations, setLocations] = useState([]);
  const { visible: visibleLocations, hide: hideLocation } = useHiddenOptions("location");
  const shownLocations = visibleLocations(locations).slice(0, 8);
  const [form, setForm] = useState(EMPTY);
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));
  const setDetail = (k) => (v) => setDetails((d) => ({ ...d, [k]: v?.target ? v.target.value : v }));

  useEffect(() => {
    if (!open) return;
    listPlants().then((ps) => {
      const counts = ps.reduce((m, p) => (p.location ? { ...m, [p.location]: (m[p.location] || 0) + 1 } : m), {});
      setLocations(Object.keys(counts).sort((a, b) => counts[b] - counts[a]));
    }).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!open || !template) return;
    const base = detailsFrom(template);
    setForm({ ...EMPTY, species: template.species || "", scientific_name: template.scientific_name || "" });
    setDetails({ ...detailsFrom(), ...Object.fromEntries(CARE_KEYS.map((k) => [k, base[k]])) });
    setStep(1);
  }, [open, template]);

  const reset = () => { reqRef.current += 1; setStep(0); setPhotoPath(null); setPreview(null); setDetails(detailsFrom()); setForm(EMPTY); setPhase(null); };
  const close = (v) => { if (!v) { reset(); setDone(null); } onOpenChange(v); };
  const [done, setDone] = useState(null);
  const navigate = useNavigate();
  const restoreDraft = (d) => { setForm(d.form); setDetails(d.details); setPhotoPath(d.photoPath); setPreview(d.photoPath ? fileUrl(d.photoPath, 960) : null); setStep(d.step); };

  const handleFile = async (file) => {
    if (!file) return;
    const req = ++reqRef.current;
    const live = () => req === reqRef.current;
    setPreview(URL.createObjectURL(file));
    setStep(1);
    setPhase("upload");
    try {
      const res = await uploadPhoto(file);
      if (!live()) return;
      setPhotoPath(res.path);
    } catch {
      if (live()) { toast.error("Upload failed"); setPreview(null); }
    } finally {
      if (live()) setPhase(null);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      const plant = await createPlant({
        nickname: form.nickname.trim(), species: form.species || null, scientific_name: form.scientific_name || null,
        location: form.location.trim() || null, notes: form.notes || null, photo_path: photoPath,
        ...detailsPayload(details), care: template?.care || null,
      });
      if (template?.ai_tip?.text) await savePlantTip(plant.id, template.ai_tip.text).catch(() => {});
      onCreated?.(plant);
      setDone({ art: "greenhouse", title: `Welcome home, ${plant.nickname}`, sub: `Saved as ${plant.plant_code}. ${template ? `${[template.care && "Care plan", template.ai_tip?.text && "recommendation"].filter(Boolean).join(" and ").replace(/^r/, "R") || "Species and pot details"} copied from ${template.nickname}.` : "Add its care plan any time from the plant page."}`, actions: [
        { label: "Open plant", onClick: () => { close(false); navigate(`/plants/${plant.id}`); } },
        ...(template ? [] : [{ label: "Add another plant", onClick: () => { reset(); setDone(null); } }]),
      ] });
    } catch {
      toast.error("Could not save plant");
    } finally {
      setSaving(false);
    }
  };

  const busy = !!phase;
  const cur = STEPS[step].key;
  const canNext = cur === "identity" ? !busy && !!form.nickname.trim() : true;
  const goTo = (k) => setStep(STEPS.findIndex((s) => s.key === k));
  const review = [
    ["identity", "Name", form.nickname],
    ["identity", "Species", [form.species, form.scientific_name].filter(Boolean).join(" · ")],
    ["home", "Location", form.location],
    ["home", "Light", LIGHT_LABELS[details.light_exposure]],
    ["home", "Health", HEALTH_STATUS[details.health_status]?.label],
    ["home", "Growth stage", GROWTH_STAGES[details.growth_stage]],
    ["origin", "Came from", [SOURCE_TYPES[details.source_type], details.source_detail].filter(Boolean).join(" · ")],
    ["origin", "Pot", [details.pot_size, details.pot_material].filter(Boolean).join(" · ")],
    ["origin", "Soil", details.soil_mix],
    ["origin", "Value", details.value !== "" ? `$${details.value}` : ""],
  ];

  return (
    <WizardShell open={open} onOpenChange={close} testid="add-plant-dialog" kicker={template ? `Copy of ${template.nickname}` : "New plant"} steps={STEPS} step={step} maxW="max-w-xl"
      dirty={step > 0 || !!preview || Object.values(form).some(Boolean) || JSON.stringify(details) !== JSON.stringify(detailsFrom())} discardText="This plant hasn't been saved yet. The photo and details you've added will be lost."
      onBack={() => setStep((s) => s - 1)} onNext={() => setStep((s) => s + 1)} canNext={canNext} hideFooter={cur === "photo"}
      draftKey={template ? null : "new-plant"} draftValue={{ form, details, photoPath, step }} onRestoreDraft={restoreDraft} done={done} doneColor="#30A14E" onSkip={() => goTo("review")}
      isLast={cur === "review"} onFinish={handleSave} saving={saving} finishLabel="Save to collection">
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" className="hidden" data-testid="plant-photo-input"
        onChange={(e) => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />

      {cur === "photo" && <PhotoStep fileRef={fileRef} onFile={handleFile} onManual={() => setStep(1)} />}

      {cur === "identity" && (
        <>
          {(preview || photoPath) ? (
            <div className="relative aspect-[16/10] rounded-2xl bg-muted overflow-hidden">
              <img src={preview || fileUrl(photoPath)} alt="Plant" className="w-full h-full object-cover" />
              <AiScanOverlay phase={phase} />
            </div>
          ) : (
            <button type="button" onClick={() => fileRef.current?.click()} data-testid="replace-photo-btn"
              className="w-full h-24 rounded-2xl bg-muted/50 hover:bg-muted flex items-center justify-center gap-2 text-xs text-muted-foreground transition-colors">
              <ImageSquare weight="duotone" className="h-5 w-5" />Add a photo
            </button>
          )}
          {(preview || photoPath) && <button type="button" data-testid="replace-photo-btn" disabled={busy} onClick={() => fileRef.current?.click()} className="mt-2 mb-2 text-xs text-muted-foreground hover:text-foreground disabled:opacity-50">Replace photo</button>}
          <div className={`transition-opacity ${busy ? "opacity-50 pointer-events-none" : ""}`}>
            <Q label="Name" hint="What the team will call it.">
              <Input data-testid="plant-nickname-input" value={form.nickname} onChange={set("nickname")} placeholder="e.g. Mabel" className={FIELD} />
            </Q>
            <Q label="Species">
              <div className="grid grid-cols-2 gap-3">
                <Input data-testid="plant-species-input" value={form.species} onChange={set("species")} placeholder="Monstera" className={FIELD} />
                <Input data-testid="plant-sci-input" value={form.scientific_name} onChange={set("scientific_name")} placeholder="Monstera deliciosa" className={`${FIELD} italic`} />
              </div>
            </Q>
          </div>
        </>
      )}

      {cur === "home" && (
        <>
          <Q label="Location">
            {shownLocations.length > 0 && <div className="mb-2.5"><ChipGroup prefix="location-chip" options={shownLocations} value={form.location} onChange={set("location")} onRemove={hideLocation} /></div>}
            <Input data-testid="plant-location-input" value={form.location} onChange={set("location")} placeholder={shownLocations.length ? "Or type a new spot" : "Living room, east window"} className={FIELD} />
          </Q>
          <Q label="Light">
            <ChipGroup prefix="light-chip" options={LIGHT} value={details.light_exposure} onChange={setDetail("light_exposure")} />
          </Q>
          <Q label="Health right now"><ChipGroup prefix="health-chip" options={Object.entries(HEALTH_STATUS).map(([k, h]) => [k, h.label, h.color])} value={details.health_status} onChange={setDetail("health_status")} /></Q>
          <Q label="Growth stage"><ChipGroup prefix="stage-chip" options={Object.entries(GROWTH_STAGES)} value={details.growth_stage} onChange={setDetail("growth_stage")} /></Q>
        </>
      )}

      {cur === "origin" && (
        <>
          <Q label="Where it came from">
            <ChipGroup prefix="source-chip" options={Object.entries(SOURCE_TYPES)} value={details.source_type} onChange={setDetail("source_type")} />
            {details.source_type && <Input data-testid="detail-source_detail-input" value={details.source_detail} onChange={setDetail("source_detail")} placeholder="Shop, seller or friend (optional)" className={`mt-3 ${FIELD}`} />}
          </Q>
          <Q label="Pot size"><ChipGroup prefix="pot-size-chip" options={POT_SIZES} value={details.pot_size} onChange={setDetail("pot_size")} /></Q>
          <Q label="Pot type"><ChipGroup prefix="pot-material-chip" options={POT_MATERIALS} value={details.pot_material} onChange={setDetail("pot_material")} /></Q>
          <Q label="Soil"><ChipGroup prefix="soil-chip" options={SOILS} value={details.soil_mix} onChange={setDetail("soil_mix")} /></Q>
          <Q label="Value" hint="Optional, in USD.">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="detail-purchase_price-input" type="number" min="0" value={details.purchase_price} onChange={setDetail("purchase_price")} placeholder="Paid" className={FIELD} />
              <Input data-testid="detail-value-input" type="number" min="0" value={details.value} onChange={setDetail("value")} placeholder="Worth now" className={FIELD} />
            </div>
          </Q>
          <Q label="Notes"><RichEditor testid="plant-notes-input" value={form.notes} onChange={set("notes")} placeholder="Anything worth remembering (optional)" minHeight={64} /></Q>
        </>
      )}

      {cur === "review" && (
        <div data-testid="plant-review">
          <div className="flex items-center gap-4">
            <span className="h-16 w-16 rounded-2xl overflow-hidden bg-muted shrink-0">
              {(preview || photoPath) ? <img src={preview || fileUrl(photoPath)} alt="" className="h-full w-full object-cover" /> : <span className="h-full w-full flex items-center justify-center"><ImageSquare weight="duotone" className="h-6 w-6 text-muted-foreground/50" /></span>}
            </span>
            <div className="min-w-0">
              <p className="text-lg font-semibold tracking-tight truncate">{form.nickname}</p>
              <p className="text-xs text-muted-foreground italic truncate">{form.scientific_name || form.species}</p>
            </div>
          </div>
          <dl className="mt-5 rounded-2xl bg-muted/40 divide-y divide-border/60">
            {review.map(([k, label, v]) => (
              <div key={label} className="flex items-center gap-4 px-4 py-3">
                <dt className="w-24 shrink-0 text-xs text-muted-foreground">{label}</dt>
                <dd className={`flex-1 min-w-0 text-sm truncate ${v ? "" : "text-muted-foreground/60"}`}>{v || "Not set"}</dd>
                <button type="button" onClick={() => goTo(k)} aria-label={`Edit ${label}`} data-testid={`review-edit-${label.toLowerCase().replace(/\s+/g, "-")}`} className="text-muted-foreground hover:text-foreground"><PencilSimple className="h-3.5 w-3.5" /></button>
              </div>
            ))}
          </dl>
        </div>
      )}
    </WizardShell>
  );
}
